import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react'
import { FormProvider, useFormState, type FieldValues, type UseFormReturn } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { AccessUnknownError } from '@/shared/access'
import { DetailsHeader, type DetailsHeaderProps } from '@/shared/details/DetailsHeader'
import { StickyActionBar } from '@/shared/layout/StickyActionBar'
import { reloadPage } from '@/shared/layout/ErrorBoundary'
import { SlotContent, SlotLoadContext, SlotPanels, useSlot, type SlotContext, type SlotLoadState } from '@/shared/registry'
import { Alert, Button, PageContainer, SectionNav, Tabs, tabPanelProps } from '@/shared/ui'

import {
  FormContributionsContext,
  FormTabContext,
  HeldBackError,
  isSameVersion,
  PartlySavedError,
  PartNotLoadedError,
  type FormContributions,
  type HostVersionAfterSave,
  type RegisteredSave,
} from './contributions'
import { invalidFieldSelector } from './focus-first-invalid'
import { SaveProblem } from './SaveProblem'
import { applyServerErrors, markSaved, useFieldErrorText, type FieldErrorText } from './server-errors'
import { useUnsavedChangesPrompt } from './unsaved-changes'

/** One section of a form: a tab, or an entry in the section list. */
export interface FormSection {
  readonly id: string
  readonly label: string
  readonly content: ReactNode
  /** A panel slot whose contributed fields follow this section's own, each saving itself. */
  readonly fieldSlot?: string
}

/** The record a save writes: `recordId` is `null` until it is created. */
export interface SaveTarget {
  readonly recordId: string | null
  /** Sent as `ifMatch`; `null` when no version was loaded. */
  readonly version: string | null
}

/** What a save gives back: the record's id, and its new version for the next save. */
export interface SavedRecord {
  readonly recordId: string
  readonly version: string | null
}

export interface RecordFormProps<V extends FieldValues, O> {
  /** The page's `useForm`, with a Zod resolver and the record's values as its defaults. */
  readonly form: UseFormReturn<V, unknown, O>
  readonly header: Omit<DetailsHeaderProps, 'actionSlot' | 'context' | 'search'>
  readonly sections: readonly FormSection[]
  /** Tabs across the top, or a section list down the side for a long form (`SectionNav`). */
  readonly layout?: 'tabs' | 'sections'
  /** A tab slot: other features' tabs follow the form's own, each saving itself. */
  readonly tabSlot?: string
  /** The section shown first, such as one a link back to the form names; the first section when absent. */
  readonly initialSection?: string
  /** The record being edited; `null` on a create page. */
  readonly recordId: string | null
  /** The version the record was loaded at. */
  readonly version: string | null
  /** Stores the host's record: a POST when `target.recordId` is `null`, else a PUT with `ifMatch`. */
  readonly save: (values: O, target: SaveTarget) => Promise<SavedRecord>
  /** Everything is saved; usually goes to the record's details page. */
  readonly onSaved: (recordId: string) => void
  readonly onCancel: () => void
  /** Loads the record again, after someone else changed it; remount the form with it. */
  readonly onReload?: () => void
  /** Saves the host record even when no field changed, for a record whose save itself means something, such as confirming flagged fields. */
  readonly isSavedWhenClean?: boolean
  /** The submit button's message ids, such as Send and Sending for a form that sends; Save and Saving when absent. */
  readonly submitLabelIds?: { readonly idle: string; readonly busy: string }
  /** The text for a refusal of the save the API names by its code alone, shown in place of the general one. */
  readonly refusals?: Readonly<Record<string, string>>
  /**
   * A second way to store the form, such as Save As: `save` stores it as a new record, then each
   * changed contribution saves to that record, and `onSaved` gets its id. Shown beside Save until
   * it has made the record; a part that then failed is saved to that record by Save. Like every
   * save, it waits for each contributed part's code to load; it makes no record while one has failed to.
   */
  readonly saveAs?: {
    readonly labelId: string
    readonly save: (values: O, target: SaveTarget) => Promise<SavedRecord & { readonly values?: V }>
    /** Save itself stores the form as a new record, as a form opened from a template does; no Save As button shows. */
    readonly isOnlyWay?: boolean
  }
}

/** How long a save waits for a contributed part's code to load. */
const slotLoadTimeoutMs = 20_000

type Problem =
  | { readonly kind: 'invalid' }
  | { readonly kind: 'host'; readonly error: unknown; readonly unplaced: readonly string[] }
  | { readonly kind: 'parts'; readonly labels: readonly string[] }
  /** `sections`: the ids of the sections holding a part whose code did not load. */
  | { readonly kind: 'unloaded'; readonly sections: readonly string[] }

/**
 * A create or edit page, laid out as the mockups' `edit-create/EditUser.jsx`: the header with
 * Save top right, the form's sections as tabs or a section list, and Cancel and Save in the
 * action bar. One Save stores the host's record, then each changed contribution (a tab or
 * fields another feature registered) through its own endpoint. A field error from the API shows
 * on its field; a 412 says someone else changed the record, with a reload; a contribution that
 * fails keeps its edits and shows its error. Leaving with unsaved changes asks first.
 */
export function RecordForm<V extends FieldValues, O>(props: RecordFormProps<V, O>) {
  const intl = useIntl()
  const errorText = useFieldErrorText()
  const formId = useId()
  const idPrefix = useId()
  const { form, sections, save, onSaved, saveAs } = props
  const isSavedWhenClean = props.isSavedWhenClean === true
  const { isDirty } = useFormState({ control: form.control })
  const contributedTabs = useSlot(props.tabSlot ?? '', 'tab')
  const [tab, setTab] = useState(props.initialSection ?? sections[0]?.id ?? '')
  const [target, setTarget] = useState<SaveTarget>({ recordId: props.recordId, version: props.version })
  const [problem, setProblem] = useState<Problem | null>(null)
  const [saving, setSaving] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // Contributions save themselves; the form keeps what to call, and which hold changes.
  const entries = useRef(new Map<string, RegisteredSave>())
  const [dirtyIds, setDirtyIds] = useState<ReadonlySet<string>>(() => new Set())
  // Kept as each contribution reports it, not as last rendered: a Save that waited for a part's code reads it at once.
  const dirtyRef = useRef(dirtyIds)
  // A Save waiting for parts to load checks again each time one loads or its changes come or go,
  // once the commit's effects have all run: a part that registers again, as when its label changes,
  // still counts as loading rather than as gone.
  const loadWaits = useRef(new Set<() => void>())
  const checkLoadWaits = useCallback(() => {
    queueMicrotask(() => {
      for (const wait of loadWaits.current) {
        wait()
      }
    })
  }, [])
  const flagDirty = useCallback(
    (id: string, dirty: boolean) => {
      dirtyRef.current = withFlag(dirtyRef.current, id, dirty)
      setDirtyIds(dirtyRef.current)
      checkLoadWaits()
    },
    [checkLoadWaits],
  )
  const hostDirty = useRef(isDirty)
  const saves = useRef({ save, saveAs })
  useEffect(() => {
    hostDirty.current = isDirty
    saves.current = { save, saveAs }
  })
  // Contributions a Save As carried to its new record whose save failed: still owed to it, changed or
  // not, until one succeeds or the contribution is read again.
  const [owedIds, setOwedIds] = useState<ReadonlySet<string>>(() => new Set())
  const owedRef = useRef(owedIds)
  useEffect(() => {
    owedRef.current = owedIds
  })
  const { prompt, allowNext } = useUnsavedChangesPrompt(isDirty || dirtyIds.size > 0 || owedIds.size > 0)

  // Each contributed part on the form, apart from its save: one whose code is still loading has not
  // joined the save yet, and one that failed never will.
  const slotLoads = useRef(new Map<string, { readonly state: SlotLoadState; readonly section: string }>())
  const onSlotLoad = useCallback(
    (key: string, section: string, state: SlotLoadState | null) => {
      if (state === null) {
        slotLoads.current.delete(key)
      } else {
        slotLoads.current.set(key, { state, section })
      }
      checkLoadWaits()
    },
    [checkLoadWaits],
  )
  // Contributions still reading what their check needs, such as the custom fields an organization has.
  const loadingIds = useRef(new Set<string>())
  const flagLoading = useCallback(
    (id: string, loading: boolean) => {
      if (loadingIds.current.has(id) === loading) {
        return
      }
      if (loading) {
        loadingIds.current.add(id)
      } else {
        loadingIds.current.delete(id)
      }
      checkLoadWaits()
    },
    [checkLoadWaits],
  )
  // Settles once no part's code is still loading, and no contribution the save takes in (`isSaved`)
  // is still reading what its check needs, with the sections of the parts whose code failed. One
  // still loading after `slotLoadTimeoutMs` counts as not loaded, so a stalled chunk or read does
  // not lock the form; one the save leaves out is not waited for.
  const partsLoaded = useCallback(
    async (isSaved: (id: string) => boolean) =>
      await new Promise<readonly string[]>((resolve) => {
        const sectionsIn = (states: readonly SlotLoadState[]) => [
          ...new Set([...slotLoads.current.values()].filter((load) => states.includes(load.state)).map((load) => load.section)),
        ]
        const finish = (unloaded: readonly string[]) => {
          clearTimeout(timer)
          loadWaits.current.delete(check)
          resolve(unloaded)
        }
        const check = () => {
          if (sectionsIn(['loading']).length === 0 && ![...loadingIds.current].some(isSaved)) {
            finish(sectionsIn(['failed']))
          }
        }
        const timer = setTimeout(() => finish(sectionsIn(['failed', 'loading'])), slotLoadTimeoutMs)
        loadWaits.current.add(check)
        check()
      }),
    [],
  )

  const contributions = useMemo<FormContributions>(
    () => ({
      register: (id, entry) => {
        entries.current.set(id, entry)
        // Registered again when its label or tab changes: what it owes a new record stays (`forgetOwed`).
        return () => {
          entries.current.delete(id)
          flagDirty(id, false)
          flagLoading(id, false)
        }
      },
      setDirty: flagDirty,
      setLoading: flagLoading,
      forgetOwed: (id) => setOwedIds((current) => withFlag(current, id, false)),
    }),
    [flagDirty, flagLoading],
  )

  // Shows the first field in error, in whichever section holds it; with none marked, the section
  // of the first contribution that did not pass (`revealSection`), such as one whose check threw.
  const [reveal, setReveal] = useState(0)
  const pendingFocus = useRef<HTMLElement | null>(null)
  const revealSection = useRef<string | null>(null)
  const sectionPanel = useCallback(
    (id: string) =>
      [...(formRef.current?.querySelectorAll<HTMLElement>('[data-form-section]') ?? [])].find(
        (panel) => panel.dataset['formSection'] === id,
      ) ?? null,
    [],
  )
  useEffect(() => {
    if (reveal === 0) {
      return
    }
    const field = formRef.current?.querySelector<HTMLElement>(invalidFieldSelector)
    const section = field?.closest<HTMLElement>('[data-form-section]')?.dataset['formSection']
    if (field === null || field === undefined || section === undefined) {
      const fallback = revealSection.current
      if (fallback !== null) {
        pendingFocus.current = sectionPanel(fallback)
        // oxlint-disable-next-line react/set-state-in-effect -- no field is marked once the errors have rendered
        setTab(fallback)
      }
      return
    }
    pendingFocus.current = field
    // oxlint-disable-next-line react/set-state-in-effect -- the field is found only once its error has rendered
    setTab(section)
  }, [reveal, sectionPanel])
  // Once its section shows and the form is no longer locked: until then it cannot take focus.
  useEffect(() => {
    const field = pendingFocus.current
    if (field !== null && field.closest('[hidden], [inert]') === null) {
      pendingFocus.current = null
      field.focus()
    }
  })

  const run = useCallback(
    async (values: O | null, sent: V, asked: boolean) => {
      // Once Save As made its record, the form saves to it.
      const asNew = (asked || saveAs?.isOnlyWay === true) && target.recordId === props.recordId
      // Every save waits for each part's code, and for each contribution it takes in that is still
      // reading what its check needs, so no part is left out of it or refused as wrong while it loads.
      // A new record is not made without a part that failed; an existing one keeps what that part
      // stored. A form with fields to fix shows them at once.
      const isSaved = (id: string) =>
        dirtyRef.current.has(id) || owedRef.current.has(id) || (asNew && entries.current.get(id)?.isCarriedToNewRecord() === true)
      const unloaded = values !== null ? await partsLoaded(isSaved) : []
      if (!mounted.current) {
        return
      }
      if (unloaded.length > 0 && (asNew || target.recordId === null)) {
        setSaving(false)
        setProblem({ kind: 'unloaded', sections: unloaded })
        const section = unloaded[0]!
        pendingFocus.current = sectionPanel(section)
        setTab(section)
        return
      }
      // A form with fields to fix checks only the parts holding changes: one carried unchanged may wait on a read.
      const carried = new Set(asNew && values !== null ? [...entries.current].filter(([, entry]) => entry.isCarriedToNewRecord()).map(([id]) => id) : [])
      const chosen = [...entries.current].filter(([id]) => dirtyRef.current.has(id) || owedRef.current.has(id) || carried.has(id))
      const pending = chosen.map(([, entry]) => entry)
      // Anything unexpected still unlocks the form, shown as the save's problem.
      const outcome: Outcome = await saveAll({
        values,
        sent,
        target,
        hostDirty: asNew || hostDirty.current || isSavedWhenClean,
        form,
        errorText,
        // The page's latest save: one from before a wait may not know what arrived since, such as permissions.
        save: async (sentValues, sentTarget) => {
          const latest = saves.current
          return await (asNew && latest.saveAs !== undefined ? latest.saveAs.save : latest.save)(sentValues, sentTarget)
        },
        entries: pending,
        everyone: [...entries.current.values()],
      }).catch((error: unknown) => ({ kind: 'host', error, unplaced: [], saved: null }))
      // Left while it saved: the page that is shown now is not this form's to change.
      if (!mounted.current) {
        return
      }
      setSaving(false)
      if (outcome.saved !== null) {
        setTarget(outcome.saved)
      }
      // Once the record was saved, what was owed to it, or carried to it now, stays owed only where its save failed.
      if (outcome.kind === 'parts' || outcome.kind === 'done') {
        const failed = new Set(outcome.kind === 'parts' ? outcome.failed : [])
        setOwedIds(new Set(chosen.filter(([id, entry]) => (owedRef.current.has(id) || carried.has(id)) && failed.has(entry)).map(([id]) => id)))
      }
      // A part whose check gave up waiting for the caller's permissions or its own reads did not load, as a part whose code did not.
      const unknown = outcome.kind === 'invalid' && values !== null ? outcome.failed.filter((entry) => outcome.notLoaded.has(entry)) : []
      if (unknown.length > 0) {
        const section = unknown[0]!.tab
        setProblem({ kind: 'unloaded', sections: [...new Set(unknown.map((entry) => entry.tab))] })
        pendingFocus.current = sectionPanel(section)
        setTab(section)
        return
      }
      switch (outcome.kind) {
        case 'invalid':
          setProblem({ kind: 'invalid' })
          revealSection.current = outcome.failed[0]?.tab ?? null
          setReveal((count) => count + 1)
          break
        case 'host':
          setProblem({ kind: 'host', error: outcome.error, unplaced: outcome.unplaced })
          revealSection.current = null
          setReveal((count) => count + 1)
          break
        case 'parts':
          // Shows the field a part marked in error, else the section holding the first part that
          // failed, and focuses it, with its error.
          setProblem({ kind: 'parts', labels: outcome.failed.map((entry) => entry.label) })
          revealSection.current = outcome.failed[0]?.tab ?? ''
          setReveal((count) => count + 1)
          break
        case 'done':
          pendingFocus.current = null
          allowNext()
          onSaved(outcome.recordId)
          break
      }
    },
    [form, errorText, saveAs, onSaved, target, allowNext, isSavedWhenClean, props.recordId, partsLoaded, sectionPanel],
  )

  // Taken at once on submit, before validation runs, so a second submit cannot start meanwhile.
  const submitting = useRef(false)
  // Set by Save As just before it submits the form, so this submit stores a new record.
  const savingAsNew = useRef(false)
  const submit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      const asNew = savingAsNew.current
      savingAsNew.current = false
      if (submitting.current) {
        event.preventDefault()
        return
      }
      submitting.current = true
      // What is saved is what the form held now; edits are locked out until the save ends.
      const sent = structuredClone(form.getValues())
      // The form is locked while it saves, which takes focus from a field in it; it goes back after.
      pendingFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      setProblem(null)
      setSaving(true)
      const check = async () => {
        let invalid = false
        await form.handleSubmit(
          async (values) => await run(values, sent, asNew),
          () => {
            invalid = true
          },
        )(event)
        // Only once handleSubmit ends are the fields in error marked, so a hidden section's can be shown.
        if (invalid) {
          await run(null, sent, asNew)
        }
      }
      void check()
        .catch((error: unknown) => {
          if (mounted.current) {
            setSaving(false)
            setProblem({ kind: 'host', error, unplaced: [] })
          }
        })
        .finally(() => {
          submitting.current = false
        })
    },
    [form, run],
  )
  const submitAsNew = useCallback(() => {
    savingAsNew.current = true
    formRef.current?.requestSubmit()
  }, [])

  // The record as the page opened it: after a create, contributions get the new id from `save`.
  const openedId = props.recordId
  const context = useMemo<SlotContext>(() => (openedId === null ? {} : { recordId: openedId }), [openedId])
  const items = useMemo(
    () => [
      ...sections.map((section) => ({ id: section.id, label: section.label })),
      ...contributedTabs.map((entry) => ({ id: entry.id, label: intl.formatMessage({ id: entry.labelId }) })),
    ],
    [sections, contributedTabs, intl],
  )
  const navigation = intl.formatMessage({ id: 'forms.sections.label' })
  const saveLabel = intl.formatMessage({ id: saving ? (props.submitLabelIds?.busy ?? 'forms.saving') : (props.submitLabelIds?.idle ?? 'forms.save') })
  const tabbed = props.layout === 'sections' || items.length > 1

  const panels = (
    <div className="min-w-0 flex-1">
      {sections.map((section) => (
        <Panel key={section.id} id={section.id} active={tab} idPrefix={idPrefix} tabbed={tabbed} onSlotLoad={onSlotLoad}>
          <div className="flex flex-col gap-1">
            {section.content}
            {section.fieldSlot !== undefined && (
              <SlotPanels slot={section.fieldSlot} context={context} className="flex flex-col gap-1" />
            )}
          </div>
        </Panel>
      ))}
      {contributedTabs.map((entry) => (
        <Panel key={entry.id} id={entry.id} active={tab} idPrefix={idPrefix} tabbed onSlotLoad={onSlotLoad}>
          <SlotContent contribution={entry} context={context} />
        </Panel>
      ))}
    </div>
  )

  return (
    <PageContainer>
      <DetailsHeader
        {...props.header}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {props.header.actions}
            {saveAs !== undefined && saveAs.isOnlyWay !== true && target.recordId === props.recordId && (
              <Button variant="outlined" size="small" onClick={submitAsNew}>
                {intl.formatMessage({ id: saveAs.labelId })}
              </Button>
            )}
            <Button type="submit" form={formId} size="small">
              {saveLabel}
            </Button>
          </div>
        }
      />

      <FormProvider {...form}>
        <FormContributionsContext value={contributions}>
          {/* Locked while it saves, so nothing changes under a save. */}
          <form id={formId} ref={formRef} noValidate onSubmit={submit} inert={saving} aria-busy={saving || undefined}>
            {problem !== null && (
              <div className="mb-6">
                {problem.kind === 'invalid' && (
                  <Alert severity="error" title={intl.formatMessage({ id: 'forms.error.invalid' })} />
                )}
                {problem.kind === 'host' && (
                  <SaveProblem
                    error={problem.error}
                    unplaced={problem.unplaced}
                    {...(props.onReload === undefined ? {} : { onReload: props.onReload })}
                    {...(props.refusals === undefined ? {} : { refusals: props.refusals })}
                  />
                )}
                {problem.kind === 'parts' && (
                  <Alert
                    severity="error"
                    title={intl.formatMessage(
                      { id: 'forms.error.parts' },
                      { parts: intl.formatList(problem.labels, { type: 'conjunction' }) },
                    )}
                  />
                )}
                {problem.kind === 'unloaded' && (
                  <Alert
                    severity="error"
                    title={intl.formatMessage(
                      { id: 'forms.error.unloaded' },
                      {
                        sections: intl.formatList(
                          problem.sections.map((id) => items.find((item) => item.id === id)?.label ?? id),
                          { type: 'conjunction' },
                        ),
                      },
                    )}
                  >
                    <Button variant="outlined" size="small" onClick={reloadPage} className="mt-3">
                      {intl.formatMessage({ id: 'forms.error.unloaded.reload' })}
                    </Button>
                  </Alert>
                )}
              </div>
            )}

            {props.layout === 'sections' ? (
              <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
                <SectionNav
                  sections={items}
                  value={tab}
                  onChange={setTab}
                  label={navigation}
                  caption={intl.formatMessage({ id: 'forms.sections.caption' })}
                  idPrefix={idPrefix}
                />
                {panels}
              </div>
            ) : (
              <>
                {items.length > 1 && (
                  <Tabs tabs={items} value={tab} onChange={setTab} label={navigation} idPrefix={idPrefix} className="mb-6" />
                )}
                {panels}
              </>
            )}
          </form>
        </FormContributionsContext>
      </FormProvider>

      <StickyActionBar info={intl.formatMessage({ id: 'forms.unsavedInfo' })}>
        <Button variant="outlined" size="small" onClick={props.onCancel}>
          {intl.formatMessage({ id: 'forms.cancel' })}
        </Button>
        <Button type="submit" form={formId} size="small">
          {saveLabel}
        </Button>
      </StickyActionBar>

      {prompt}
    </PageContainer>
  )
}

type Outcome = {
  /** The host's record as just saved; `null` when it was not saved this time. */
  readonly saved: SavedRecord | null
} & (
  /**
   * `failed`: the contributions whose check did not pass, by resolving `false` or throwing;
   * `notLoaded`: those whose check gave up waiting for the caller's permissions or its own reads.
   */
  | { readonly kind: 'invalid'; readonly failed: readonly RegisteredSave[]; readonly notLoaded: ReadonlySet<RegisteredSave> }
  | { readonly kind: 'host'; readonly error: unknown; readonly unplaced: readonly string[] }
  | { readonly kind: 'parts'; readonly failed: readonly RegisteredSave[] }
  | { readonly kind: 'done'; readonly recordId: string }
)

/**
 * Checks every changed contribution, then stores the host's record (when new or changed), then
 * each changed contribution with the record's id: those sharing the host's version one after
 * another, the rest together. Nothing is stored while any field is wrong.
 */
async function saveAll<V extends FieldValues, O>(args: {
  readonly values: O | null
  readonly sent: V
  readonly target: SaveTarget
  readonly hostDirty: boolean
  readonly form: UseFormReturn<V, unknown, O>
  readonly errorText: FieldErrorText
  /** Its `values`, when given, are the form's values as the record now holds them, such as a copy's. */
  readonly save: (values: O, target: SaveTarget) => Promise<SavedRecord & { readonly values?: V }>
  readonly entries: readonly RegisteredSave[]
  /** Every contribution, changed or not, to hear the host's new version. */
  readonly everyone: readonly RegisteredSave[]
}): Promise<Outcome> {
  const { values, form, errorText, entries } = args
  for (const entry of entries) {
    entry.showError(null)
  }
  // A check that throws counts as failed, and shows its error in its contribution. One still loading
  // after the form waited for it did not load; while the host's own fields need fixing nothing is
  // saved, so it is not checked and those fields show at once.
  const checks = await Promise.allSettled(
    entries.map(async (entry) => {
      if (entry.isLoading()) {
        if (values === null) {
          return true
        }
        throw new PartNotLoadedError()
      }
      return await entry.validate({ isHostValid: values !== null })
    }),
  )
  const notPassed: RegisteredSave[] = []
  const notLoaded = new Set<RegisteredSave>()
  checks.forEach((check, index) => {
    const entry = entries[index]
    if (entry === undefined || (check.status === 'fulfilled' && check.value)) {
      return
    }
    if (check.status === 'rejected') {
      if (check.reason instanceof AccessUnknownError || check.reason instanceof PartNotLoadedError) {
        notLoaded.add(entry)
      } else {
        entry.showError(check.reason)
      }
    }
    notPassed.push(entry)
  })
  if (values === null || notPassed.length > 0) {
    return { kind: 'invalid', failed: notPassed, notLoaded, saved: null }
  }

  let saved: SavedRecord | null = null
  let recordId = args.target.recordId
  if (recordId === null || args.hostDirty) {
    try {
      const stored = await args.save(values, args.target)
      saved = { recordId: stored.recordId, version: stored.version }
      recordId = stored.recordId
      // The form is locked while it saves, so the record's own values, such as a copy's, hold no edit.
      if (stored.values === undefined) {
        markSaved(form, args.sent)
      } else {
        form.reset(stored.values)
      }
      // A new record, as Save As makes, is every contribution's to save to, whatever version it held.
      const from = stored.recordId === args.target.recordId ? args.target.version : undefined
      for (const entry of args.everyone) {
        entry.hostSaved(stored.version, from)
      }
    } catch (error: unknown) {
      return { kind: 'host', error, unplaced: applyServerErrors(form, error, errorText), saved: null }
    }
  }

  const id = recordId
  // The version the form is at: it takes a part's new version only from a write made at this one.
  let hostAt = saved === null ? args.target.version : saved.version
  const passOn = (result: PromiseSettledResult<HostVersionAfterSave | void>) => {
    const moved = hostVersionOf(result)
    if (moved === undefined) {
      return
    }
    const from = result.status === 'fulfilled' ? result.value?.fromVersion : undefined
    if (from === undefined || isSameVersion(from, hostAt)) {
      hostAt = moved
      saved = { recordId: id, version: moved }
    }
    for (const other of args.everyone) {
      other.hostSaved(moved, from)
    }
  }
  const results = new Map<RegisteredSave, PromiseSettledResult<HostVersionAfterSave | void>>()
  // One that may end the viewer's access saves after all the rest, which could not save after it,
  // and only once they all saved, so the viewer can still save again whatever failed.
  const last = entries.filter((entry) => entry.savesLast())
  const first = entries.filter((entry) => !last.includes(entry))
  // Contributions with endpoints of their own save together.
  const apart = first.filter((entry) => !entry.isSharingHostVersion)
  const settled = await Promise.allSettled(apart.map(async (entry) => await entry.save(id)))
  apart.forEach((entry, index) => results.set(entry, settled[index]!))
  settled.forEach(passOn)
  // Those sharing the host's version save one after another: each one that moved it, even one that
  // then failed in part, hands it on before the next saves, to those at the version it started from.
  for (const entry of [...first.filter((candidate) => candidate.isSharingHostVersion), ...last]) {
    if (last.includes(entry) && [...results.values()].some((result) => result.status === 'rejected')) {
      results.set(entry, { status: 'rejected', reason: new HeldBackError() })
      continue
    }
    let result: PromiseSettledResult<HostVersionAfterSave | void>
    try {
      result = { status: 'fulfilled', value: await entry.save(id) }
    } catch (reason: unknown) {
      result = { status: 'rejected', reason }
    }
    results.set(entry, result)
    passOn(result)
  }
  const failed = entries.filter((entry) => {
    const result = results.get(entry)
    if (result?.status === 'rejected') {
      entry.showError(result.reason instanceof PartlySavedError ? result.reason.error : result.reason)
      return true
    }
    return false
  })
  // The form opens the first part that failed, so one held back comes after what made it wait.
  const heldBack = (entry: RegisteredSave) => {
    const result = results.get(entry)
    return result?.status === 'rejected' && result.reason instanceof HeldBackError
  }
  return failed.length > 0
    ? { kind: 'parts', failed: [...failed.filter((entry) => !heldBack(entry)), ...failed.filter(heldBack)], saved }
    : { kind: 'done', recordId: id, saved }
}

/** The host's version a contribution's save left, even one that failed in part; `undefined` when it moved none. */
function hostVersionOf(result: PromiseSettledResult<HostVersionAfterSave | void>): string | null | undefined {
  if (result.status === 'fulfilled') {
    return result.value?.hostVersion
  }
  return result.reason instanceof PartlySavedError ? result.reason.hostVersion : undefined
}

/** One section's panel. Every panel stays mounted, so edits in a hidden one are kept. */
function Panel(props: {
  readonly id: string
  readonly active: string
  readonly idPrefix: string
  readonly tabbed: boolean
  /** Hears each contributed part in it load, show or fail, with this section's id. */
  readonly onSlotLoad: (key: string, section: string, state: SlotLoadState | null) => void
  readonly children: ReactNode
}) {
  const { id, onSlotLoad } = props
  const hidden = id !== props.active
  const reportLoad = useCallback((key: string, state: SlotLoadState | null) => onSlotLoad(key, id, state), [onSlotLoad, id])
  return (
    <FormTabContext value={id}>
      <SlotLoadContext value={reportLoad}>
        <div
          {...(props.tabbed ? tabPanelProps(props.idPrefix, id) : {})}
          data-form-section={id}
          hidden={hidden}
        >
          {props.children}
        </div>
      </SlotLoadContext>
    </FormTabContext>
  )
}

function withFlag(set: ReadonlySet<string>, id: string, on: boolean): ReadonlySet<string> {
  if (set.has(id) === on) {
    return set
  }
  const next = new Set(set)
  if (on) {
    next.add(id)
  } else {
    next.delete(id)
  }
  return next
}
